// Qué hace falta para ver/abrir CADA sección del panel de administración.
//
// Antes la entrada al panel y todas sus secciones dependían del ROL (RoleRoute roles={['admin']}),
// con dos consecuencias que reportó la clínica: (1) quien tenía el permiso de Informes o de Caja no
// podía llegar a esas pantallas porque viven dentro del panel, y (2) cuando entraba por Caja el menú
// le pintaba las 24 secciones aunque al pulsarlas se le expulsara de vuelta a la app — una fuga de
// información sobre el sistema y una experiencia pésima.
//
// Este mapa es la ÚNICA fuente: lo leen a la vez el menú (components/AdminLayout.jsx) y el enrutador
// (App.jsx), así no pueden discrepar. Si una entrada se pinta, su ruta deja pasar; si no se pinta,
// la ruta rechaza. El servidor comprueba lo mismo por su cuenta (requireModulo / esAdmin dentro de
// cada router): que el menú no lo pinte NO es la seguridad, solo la cara visible.
//
//   perm       → clave del catálogo de permisos (backend/services/permisos.js). Es lo que el admin
//                configura en Ajustes > Usuarios, doctores y permisos, por rol o por usuario.
//   soloAdmin  → secciones cuya API exige ser administrador DENTRO del router (ajustes, textos
//                legales, RGPD, seguridad, gabinetes, control horario...). No tienen permiso propio
//                todavía, así que darles uno solo serviría para enseñar una pantalla que la API
//                rechazaría: exactamente el bug que esto arregla. Siguen siendo de administrador.
export const ADMIN_SECCIONES = {
  '/administracion': { soloAdmin: true },
  '/ajustes': { soloAdmin: true },            // todas sus áreas (?area=clinica, usuarios, facturacion...)
  '/horario-clinica': { soloAdmin: true },
  '/gabinetes': { soloAdmin: true },
  '/control-horario': { soloAdmin: true },
  '/notificaciones': { soloAdmin: true },
  '/automatismos': { soloAdmin: true },
  '/plantillas': { soloAdmin: true },
  '/textos-legales': { soloAdmin: true },
  // t330 · qué se le pide al paciente en el pre-registro: lo guarda solo el administrador (routes/preRegistroCampos · esAdmin),
  // igual que la compañía aseguradora del formulario.
  '/pre-registro-campos': { soloAdmin: true },
  // t380 · completar los representantes de los menores desde sus pre-registros: solo el administrador (routes/preRegistroRepresentantes).
  '/pre-registro-representantes': { soloAdmin: true },
  // AQUI VIVIO '/condiciones-clinicas'. Retirado el 19-sep-2026 con su pantalla y su ruta: el
  // catalogo de circunstancias es del sistema anterior. Una entrada aqui sin ruta detras no rompe
  // nada, pero describe un permiso de algo que ya no se puede abrir.
  // B-284/B-345 · subir un PDF y que la IA saque la plantilla entera. Sus cuatro puertas exigen
  // administrador dentro del router (routes/plantillasPdf.js, `soloAdmin`), asi que aqui no cabe
  // otra cosa. Y cada analisis es una llamada de pago: el candado de verdad esta en el servidor.
  '/plantillas-pdf': { soloAdmin: true },
  // B-139 · los campos de las dos hojas del anestesista (se llega desde Plantillas). Guardarlos exige administrador en
  // el servidor (routes/anestesiaClinica.js), así que aquí no cabe otra cosa.
  '/plantillas-anestesia': { soloAdmin: true },
  '/seguridad': { soloAdmin: true },
  '/superadmin': { soloSuperadmin: true },
  // Con permiso propio: el admin puede delegarlas a un rol o a una persona y funcionan de verdad,
  // porque su API ya se monta con requireModulo() de esa misma clave.
  '/informes': { perm: 'informes' },                  // /api/informes
  '/valor-clinica': { perm: 'valor_clinica' },        // /api/valor-clinica (t376; el gerente, solo si se le da a mano)
  '/direccion': { perm: 'coste_sillon' },             // /api/coste-sillon (t375, Dirección; el gerente, solo si se le da a mano)
  '/contabilidad': { perm: 'contabilidad' },          // /api/contabilidad
  '/catalogo-tratamientos': { perm: 'catalogo' },     // /api/catalogo-tratamientos
  '/asistencias': { perm: 'asistencias' },            // /api/asistencias
  '/dispositivos': { perm: 'tablets' },               // /api/dispositivos (antes /tablets, que lleva aquí)
  '/pantalla-sala': { perm: 'tablets' },              // t298 · /api/pantallas-sala (las teles de la sala, con el permiso de los dispositivos)
  // '/caja' estuvo aquí hasta el 16-sep-2026 (B-529): la Caja ha salido del panel y se abre desde el
  // menú lateral de la aplicación con su permiso 'caja' (components/Layout.jsx). Tener aquí una
  // sección que ya no está en el panel pintaría el botón «Panel de administración» a recepción solo
  // para llevarla a una pantalla que ya tiene en su menú.
  // El libro de estupefacientes lo LEE quien lleva el almacén (es quien da de alta las compras y
  // quien tiene que cuadrar el saldo); escribir un asiento a mano o anular uno sigue siendo de
  // administrador y eso lo comprueba el servidor, no este mapa.
  // Y ADEMÁS QUIEN PUEDE VER PACIENTES (el dueño, 24-sep: «lo abre quien puede ver pacientes: al almacén
  // no le sale el botón»). El libro dice a qué paciente se le puso cada ampolla, y su API pide
  // «clientes»: con solo «stock», el botón salía y al abrirlo daba error. `tambien` pide los dos a la
  // vez sin pintar la puerta del panel a quien solo ve pacientes (recepción no lleva el libro).
  '/libro-estupefacientes': { perm: 'stock', tambien: 'clientes' },        // /api/anestesia-libro
  '/observabilidad': { perm: 'usuarios' },            // su pantalla tira de /api/admin (clave 'usuarios')
  // LA PAPELERA ES EL PERMISO MÁS DELEGABLE QUE HAY y no se podía delegar. Su clave existe para que
  // el dueño «pueda contratar a alguien para el soporte y darle EXACTAMENTE esto, sin darle la
  // administración de la clínica» (backend/services/papeleraPermiso.js). La entrada estaba en el menú
  // de DENTRO del panel (components/AdminLayout.jsx, perm: 'papelera') y la puerta de FUERA —si se
  // pinta «Panel de administración» y a dónde lleva— sale de este mapa, donde /papelera no estaba: a
  // quien no tuviera ADEMÁS otra sección del panel, concederle la papelera no le abría ninguna puerta.
  '/papelera': { perm: 'papelera' },                  // /api/papelera (el router comprueba la clave en cada petición)
  // El gasto de WhatsApp de la clínica: misma clave que monta su API en index.js
  // (requireModulo('conversaciones')). Quien lleva el WhatsApp puede ver lo que cuesta; cambiar el
  // presupuesto y los avisos sigue siendo de administrador y lo decide el servidor.
  '/coste-whatsapp': { perm: 'conversaciones' },      // /api/wa-costes
  // B-940 · Tu cuenta de Biango: lo que Biango le factura a la clínica. Su API exige administrador (requireAdminOSuper).
  '/cuenta-biango': { soloAdmin: true },
  // EJERCICIO DE DERECHOS (RGPD). Estaba aquí como `/rgpd: soloAdmin`, y ese nombre de ruta pasa a
  // ser el de la página PÚBLICA que la clínica enlaza en su web. La bandeja del equipo es ahora
  // /ejercicio-derechos y tiene clave propia y delegable: quien recibe estas solicitudes es el
  // mostrador, y el plazo legal de un mes empieza a correr el día que entran.
  '/ejercicio-derechos': { perm: 'rgpd_derechos' },   // /api/rgpd
  // ── LOS CONSENTIMIENTOS INFORMADOS: EL MISMO CASO QUE LA PAPELERA (B-035, 21-sep-2026) ───────
  //  El dueño: «hay páginas que un usuario las tiene activas en panel de administración pero no le
  //  salen en el sidebar». Este es el caso más limpio de los que había.
  //
  //  La entrada de DENTRO del panel (components/AdminLayout) se pinta con su permiso propio
  //  `consentimientos_proponer`, que de fábrica tienen el administrador Y EL COLABORADOR — el propio
  //  comentario de allí dice «quien solo puede proponer TIENE que ver la entrada». Pero la PUERTA de
  //  fuera —si se pinta el botón «Panel de administración» y a dónde lleva— sale de ESTE mapa, y aquí
  //  no estaba. Resultado: el colaborador tenía la pantalla concedida, la entrada existía dentro, y
  //  no había forma de llegar: sus otras secciones del panel son todas de administrador, así que no
  //  se le pintaba ningún botón. La pantalla existía para él y era inalcanzable.
  //
  //  Es LITERALMENTE lo que le pasó a `/papelera` (ver el párrafo de arriba) y se arregló igual.
  '/consentimientos-informados': { perm: 'consentimientos_proponer' },
  // B-233 · las preguntas de la historia de salud: con la MISMA clave que exige su API para cambiarlas. Sin estar aquí,
  // a quien se le diera ese permiso sin ninguna otra sección del panel no le saldría el botón (lo de /papelera).
  '/cuestionario-salud': { perm: 'consentimientos_aprobar' },
}

// ¿Puede este usuario ver/abrir esta sección del panel?
// - El administrador entra a todo (invariante del sistema de permisos: al admin no se le bloquea).
// - El resto necesita el permiso EXPLÍCITO en true. En el MENÚ eso significa que, mientras los
//   permisos cargan, un no-admin no ve entradas (mejor eso que enseñar y quitar). Las RUTAS usan
//   PermRoute, que solo expulsa cuando el permiso es explícitamente false, para no rebotar a nadie
//   durante la carga en un refresco de página.
// - Una sección que no esté en el mapa se considera de administrador: si mañana se añade una entrada
//   al menú y se olvida ponerla aquí, se queda cerrada de más, nunca abierta de más.
// - EL ACCESO DE SOLO CONSULTA. Un módulo puede tener una clave HERMANA de solo lectura (hoy
//   `contabilidad_lectura`, la que la clínica le da a su gestoría). Quien tiene solo la hermana tiene
//   el booleano de la clave principal en `false` —es la verdad, no se le ha concedido— y este mapa lo
//   dejaba fuera del menú: la clínica concedía el permiso, el gestor entraba y no veía la entrada por
//   ninguna parte. El servidor manda el acceso YA RESUELTO en `_accesos`
//   ('completo' | 'lectura' | 'ninguno'), calculado con la ÚNICA definición que hay
//   (backend/services/permisos.js, `resolverAcceso`), la misma con la que las rutas contestan 403.
//   Aquí se lee, nunca se deduce: deducirlo sería tener la misma decisión escrita en dos sitios.
// - (6-oct-2026) SALVO UN MÓDULO APAGADO. El dueño: «lo desactivo también de Sistemas de la clínica y sigue apareciendo la
//   tarjeta en el inicio de admin». Un sistema apagado (por la clínica, por Biango o porque su licencia no lo incluye) le
//   llega al administrador con su clave a `false` —es la ÚNICA forma de que al administrador le llegue un `false`: para él
//   el servidor pone todo a `true` y luego cierra lo apagado (backend/services/permisos · aplicarSistemasApagados)—, y aquí
//   se entraba por delante sin mirarlo. Ahora el administrador entra a todo MENOS a lo apagado: `!== false` (mientras los
//   permisos no han llegado, `undefined`, se le sigue enseñando como siempre). Lo mismo el menú del panel: `adminOPermiso`.
export const adminOPermiso = (esAdmin, perms, clave) => (esAdmin ? perms?.[clave] !== false : perms?.[clave] === true)

export function puedeSeccionAdmin(ruta, ctx) {
  const s = ADMIN_SECCIONES[String(ruta || '').split('?')[0]]
  const { esAdmin, esSuperadmin, perms } = ctx || {}
  if (s?.soloSuperadmin) return esSuperadmin === true
  if (esAdmin) return !(s?.perm && perms?.[s.perm] === false)
  if (!s || s.soloAdmin) return false
  // UN SEGUNDO PERMISO, CUANDO LA SECCIÓN LO PIDE (24-sep, el libro de estupefacientes): sin él, no.
  if (s.tambien && !(perms?.[s.tambien] === true || ['completo', 'lectura'].includes(perms?._accesos?.[s.tambien]))) return false
  const acceso = perms?._accesos?.[s.perm]
  if (acceso === 'completo' || acceso === 'lectura') return true
  return perms?.[s.perm] === true
}

export default puedeSeccionAdmin
